import { Formik } from 'formik';
import React, { useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { FormGroup, InlineNotification, Stack } from '@carbon/react';
import EmployeeRuleSelector from 'src/components/Form/EmployeeRuleSelector';
import EmployeeSelector from 'src/components/Form/EmployeeSelector';
import { FormikTextInput } from 'src/components/Form/FormikInputs';
import { FormikRadioButtonGroup } from 'src/components/Form/FormikRadioButtonGroup';
import MoneyInput from 'src/components/Form/MoneyInput';
import RestaurantBranchSelector from 'src/components/Form/RestaurantBranchSelector';
import FileListUploader from 'src/components/Uploader/FileListUploader';
import { useEmployeeRules } from 'src/hooks/useEmployeeRules';
import { useCurrentRestaurantBranch } from 'src/hooks/useRestaurantBranches';
import { IssueEntity } from 'src/services/apis/types';
import { IssueStatusEnum, IssueStatuses, IssueTypeEnum, IssueTypes } from 'src/utils/issue';

interface IssueFormProps {
  onSubmit: (values: any) => void;
  errorMessage: string | null;
  type?: IssueTypeEnum;
  initialValues: IssueEntity | null | undefined;
}

const BaseSchema = z.object({
  title: z.string().max(50).trim(),
  fine: z.preprocess((a) => parseFloat(`${a}`), z.number().min(0)),
});

const IssueForm = (props: IssueFormProps, ref) => {
  const { t } = useTranslation();
  const formikRef = useRef<any>();
  const currentRestaurantBranch = useCurrentRestaurantBranch();
  const { data: employeeRules } = useEmployeeRules();
  const [submitted, setSubmitted] = useState(false);

  useImperativeHandle(ref, () => {
    return {
      submitForm: () => {
        setSubmitted(true);
        formikRef.current?.submitForm();
      },
      setFieldValue: formikRef.current?.setFieldValue,
      resetForm: () => {
        setSubmitted(false);
        formikRef.current?.resetForm({
          values: {
            id: '',
            restaurantBranchId: '',
            employeeId: '',
            employeeRuleId: '',
            fine: 0,
            title: '',
            notes: '',
            description: '',
            type: '',
            status: '',
            tags: '',
            attachments: [],
          },
        });
      },
    };
  });

  const getDefaultStatus = () => {
    return IssueStatusEnum.OPEN;
  };

  const initialType = props.initialValues?.type || props.type || IssueTypeEnum.EMPLOYEE;
  // The server now always keeps an Issue's own branch on PATCH/PUT (commit 04dcef1,
  // selfScopedIssuesCrudAuth persist + IssuesService.branchColumn). A changed value here would
  // be silently dropped, so the selector must not look editable on edit.
  const isEdit = !!props.initialValues;

  return (
    <div>
      {props.errorMessage && <InlineNotification subtitle={props.errorMessage} />}
      <Formik
        innerRef={formikRef}
        enableReinitialize
        validationSchema={toFormikValidationSchema(BaseSchema)}
        initialValues={{
          id: props.initialValues?.id,
          restaurantBranchId: props.initialValues?.restaurantBranchId || currentRestaurantBranch?.id,
          employeeId: props.initialValues?.employeeId,
          employeeRuleId: props.initialValues?.employeeRuleId,
          fine: props.initialValues ? props.initialValues?.fine : 0,
          title: props.initialValues?.title,
          notes: props.initialValues?.notes,
          description: props.initialValues?.description,
          type: initialType,
          status: props.initialValues?.status || getDefaultStatus(),
          tags: props.initialValues?.tags ? props.initialValues?.tags.join(',') : '',
          attachments: props.initialValues?.attachments ?? [],
        }}
        onSubmit={props.onSubmit}
      >
        {({ values, setFieldValue }) => (
          <FormGroup legendText={''}>
            <Stack gap={5}>
              <RestaurantBranchSelector
                value={values.restaurantBranchId}
                onChange={(value) => {
                  setFieldValue('restaurantBranchId', value);
                }}
                disabled={isEdit}
              />
              <FormikRadioButtonGroup
                name={'type'}
                label={t('common.type')}
                value={values.type!}
                useTranslation
                onChange={(value) => {
                  setFieldValue('type', value);
                }}
                options={IssueTypes}
              />
              {values.type === IssueTypeEnum.EMPLOYEE && (
                <EmployeeSelector
                  value={values.employeeId}
                  invalid={submitted && !values.employeeId}
                  branchId={values.restaurantBranchId}
                  onChange={(value) => {
                    setFieldValue('employeeId', value);
                  }}
                />
              )}
              {values.type === IssueTypeEnum.EMPLOYEE && (
                <EmployeeRuleSelector
                  value={values.employeeRuleId}
                  onChange={(value) => {
                    setFieldValue('employeeRuleId', value);
                    if (!props.initialValues?.title) {
                      setFieldValue('title', employeeRules?.data?.find((item) => item.id === value)?.title);
                    }
                    if (!props.initialValues?.description) {
                      setFieldValue('description', employeeRules?.data?.find((item) => item.id === value)?.description);
                    }
                    if (!props.initialValues?.fine) {
                      setFieldValue('fine', employeeRules?.data?.find((item) => item.id === value)?.fine || 0);
                    }
                  }}
                />
              )}
              <FormikTextInput name={'title'} label={t('common.title')} />
              <FormikTextInput name={'description'} label={t('common.description')} />
              {values.type === IssueTypeEnum.EMPLOYEE && <MoneyInput name={'fine'} label={t('common.fine')} />}
              <FormikTextInput name={'notes'} label={t('common.notes')} />
              <FormikTextInput name={'tags'} label={t('common.tags')} />
              <FormikRadioButtonGroup
                name={'status'}
                useTranslation
                label={t('common.status')}
                value={values.status!}
                onChange={(value) => setFieldValue('status', value)}
                options={IssueStatuses}
              />
              <FileListUploader
                label={t('common.attachments')}
                value={values.attachments ?? []}
                onChange={(keys) => setFieldValue('attachments', keys)}
              />
            </Stack>
          </FormGroup>
        )}
      </Formik>
    </div>
  );
};

export default React.forwardRef(IssueForm);
